import React, {useEffect, useState} from 'react';
import {useNavigate, useParams} from 'react-router-dom';
import {Notify, Confirm, Loading} from 'notiflix';
import moment from 'moment';
import {
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Divider,
  Grid,
  Typography,
  Alert,
} from '@mui/material';
import {
  ArrowBack,
  Delete as DeleteIcon,
  Edit as EditIcon,
  CalendarToday,
  Update,
  Category,
} from '@mui/icons-material';
import MDBox from 'components/MDBox';
import DashboardLayout from 'examples/LayoutContainers/DashboardLayout';
import DashboardNavbar from 'examples/Navbars/DashboardNavbar';
import Footer from 'examples/Footer';
import {understandItBetterApi} from 'api/understandItBetterApi';
import usePermission from 'hooks/usePermission';

const ENTITY_TYPE_META = {
  products: {
    label: 'Product',
    color: '#1565c0',
    bg: '#e3f2fd',
    lightBg: '#f0f7ff',
  },
  valuepacks: {
    label: 'Value Pack',
    color: '#7b1fa2',
    bg: '#f3e5f5',
    lightBg: '#fdf4ff',
  },
  plans: {
    label: 'Question & Answer',
    color: '#2e7d32',
    bg: '#e8f5e9',
    lightBg: '#f0fdf4',
  },
};

const UnderstandItBetterView = () => {
  const {entityType, entityId} = useParams(); // ← entity level
  const navigate = useNavigate();
  const canUpdate = usePermission('shop-management', 'update');
  const canDelete = usePermission('shop-management', 'delete');

  const [sections, setSections] = useState([]);
  const [entityName, setEntityName] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [deleting, setDeleting] = useState(false);

  const entityMeta = ENTITY_TYPE_META[entityType] || {
    label: entityType,
    color: '#475569',
    bg: '#f1f5f9',
    lightBg: '#f8fafc',
  };

  useEffect(() => {
    if (entityType && entityId) fetchSections();
  }, [entityType, entityId]);

  const fetchSections = async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await understandItBetterApi.getByEntity(
        entityType,
        entityId,
      );
      const list = Array.isArray(data) ? data : [];
      setSections(list);
      if (list.length > 0) setEntityName(list[0].entityName || '');
    } catch (err) {
      setError(err.message || 'Failed to load sections');
      Notify.failure('Failed to load section details');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteAll = () => {
    Confirm.show(
      'Delete All Sections',
      `"${
        entityName || entityId
      }" ke saare sections delete ho jayenge. Kya aap sure hain?`,
      'Delete All',
      'Cancel',
      async () => {
        setDeleting(true);
        Loading.circle('Deleting...');
        try {
          await understandItBetterApi.deleteAllByEntity(entityType, entityId);
          Notify.success('All sections deleted successfully');
          navigate('/admin/understand-it-better');
        } catch (err) {
          Notify.failure(err.message || 'Failed to delete');
        } finally {
          Loading.remove();
          setDeleting(false);
        }
      },
      {okButtonBackground: '#ff6b6b', titleColor: '#ff6b6b'},
    );
  };

  // ─── Loading ───────────────────────────────────────────────────────────────
  if (loading) {
    return (
      <DashboardLayout>
        <DashboardNavbar />
        <Box
          display="flex"
          justifyContent="center"
          alignItems="center"
          minHeight="60vh"
          flexDirection="column">
          <CircularProgress />
          <Typography variant="h6" mt={2}>
            Loading sections...
          </Typography>
        </Box>
        <Footer />
      </DashboardLayout>
    );
  }

  // ─── Error ─────────────────────────────────────────────────────────────────
  if (error || sections.length === 0) {
    return (
      <DashboardLayout>
        <DashboardNavbar />
        <Box p={4}>
          <Alert
            severity="error"
            action={
              <Button
                color="inherit"
                size="small"
                onClick={() => navigate('/admin/understand-it-better')}>
                Back to List
              </Button>
            }>
            {error || 'No sections found for this entity'}
          </Alert>
        </Box>
        <Footer />
      </DashboardLayout>
    );
  }

  // ─── Render ────────────────────────────────────────────────────────────────
  return (
    <DashboardLayout>
      <DashboardNavbar />
      <MDBox pt={6} pb={3}>
        <Box mx="auto" px={2}>
          {/* Header */}
          <Box
            display="flex"
            alignItems="center"
            justifyContent="space-between"
            mb={4}
            flexWrap="wrap"
            gap={2}>
            <Button
              startIcon={<ArrowBack />}
              onClick={() => navigate('/admin/understand-it-better')}>
              Back to List
            </Button>
            <Box display="flex" gap={1.5}>
              {canUpdate && (
                <Button
                  variant="outlined"
                  startIcon={<EditIcon />}
                  onClick={() =>
                    navigate(
                      `/admin/understand-it-better/edit/${entityType}/${entityId}`,
                    )
                  }
                  sx={{borderColor: '#1976d2', color: '#1976d2'}}>
                  Edit Sections
                </Button>
              )}
              {canDelete && (
                <Button
                  variant="outlined"
                  startIcon={
                    deleting ? <CircularProgress size={16} /> : <DeleteIcon />
                  }
                  onClick={handleDeleteAll}
                  disabled={deleting}
                  sx={{
                    borderColor: '#ef4444',
                    color: '#ef4444',
                    '&:hover': {bgcolor: '#fef2f2', borderColor: '#dc2626'},
                  }}>
                  Delete All
                </Button>
              )}
            </Box>
          </Box>

          {/* Entity Info Card */}
          <Card sx={{mb: 3, overflow: 'hidden'}}>
            <Box sx={{bgcolor: entityMeta.color, p: 3}}>
              <Typography
                variant="h5"
                fontWeight={700}
                color="#fff"
                gutterBottom>
                {entityName || entityId}
              </Typography>
              <Box display="flex" alignItems="center" gap={1.5}>
                <Chip
                  label={entityMeta.label}
                  size="small"
                  sx={{
                    bgcolor: 'rgba(255,255,255,0.2)',
                    color: '#fff',
                    fontWeight: 600,
                  }}
                />
                <Chip
                  label={`${sections.length} Sections`}
                  size="small"
                  sx={{
                    bgcolor: 'rgba(255,255,255,0.2)',
                    color: '#fff',
                    fontWeight: 600,
                  }}
                />
              </Box>
            </Box>

            <CardContent sx={{p: 3}}>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <Box display="flex" alignItems="center" gap={1}>
                    <Category sx={{color: '#64748b', fontSize: 18}} />
                    <Typography
                      variant="caption"
                      color="textSecondary"
                      fontWeight={600}
                      textTransform="uppercase">
                      Entity Type
                    </Typography>
                  </Box>
                  <Chip
                    label={entityMeta.label}
                    size="small"
                    sx={{
                      bgcolor: entityMeta.bg,
                      color: entityMeta.color,
                      fontWeight: 600,
                      mt: 0.5,
                    }}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Box display="flex" alignItems="center" gap={1}>
                    <CalendarToday sx={{color: '#64748b', fontSize: 18}} />
                    <Typography
                      variant="caption"
                      color="textSecondary"
                      fontWeight={600}
                      textTransform="uppercase">
                      Last Updated
                    </Typography>
                  </Box>
                  <Typography variant="body2" color="#1e293b" mt={0.5}>
                    {sections[0]?.updatedAt
                      ? moment(sections[0].updatedAt).format(
                          'DD MMM YYYY, hh:mm A',
                        )
                      : 'N/A'}
                  </Typography>
                </Grid>
              </Grid>
            </CardContent>
          </Card>

          {/* Sections List */}
          <Typography variant="h6" fontWeight={700} color="#1e3a5f" mb={2}>
            Sections ({sections.length})
          </Typography>

          <Box display="flex" flexDirection="column" gap={2} mb={3}>
            {sections.map((section, index) => (
              <Card
                key={section.id}
                sx={{
                  border: '1px solid #e2e8f0',
                  '&:hover': {boxShadow: '0 4px 16px rgba(0,0,0,0.08)'},
                }}>
                <CardContent sx={{p: 3}}>
                  <Box
                    display="flex"
                    alignItems="center"
                    justifyContent="space-between"
                    mb={2}>
                    <Box display="flex" alignItems="center" gap={1.5}>
                      <Box
                        sx={{
                          width: 28,
                          height: 28,
                          borderRadius: '50%',
                          bgcolor: entityMeta.color,
                          color: '#fff',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          fontWeight: 700,
                          fontSize: 12,
                          flexShrink: 0,
                        }}>
                        {index + 1}
                      </Box>
                      <Typography
                        variant="subtitle1"
                        fontWeight={700}
                        color="#1e3a5f">
                        {section.title}
                      </Typography>
                    </Box>
                    <Chip
                      label={section.status !== false ? 'Active' : 'Inactive'}
                      size="small"
                      color={section.status !== false ? 'success' : 'error'}
                      variant={section.status !== false ? 'filled' : 'outlined'}
                    />
                  </Box>

                  <Box
                    sx={{
                      bgcolor: entityMeta.lightBg,
                      border: `1px solid ${entityMeta.color}20`,
                      borderRadius: 1.5,
                      p: 2,
                    }}>
                    <Typography
                      variant="body2"
                      color="#374151"
                      lineHeight={1.8}>
                      {section.description}
                    </Typography>
                  </Box>

                  <Box display="flex" gap={3} mt={1.5}>
                    <Typography variant="caption" color="textSecondary">
                      Created:{' '}
                      {section.createdAt
                        ? moment(section.createdAt).format('DD MMM YYYY')
                        : 'N/A'}
                    </Typography>
                    <Typography variant="caption" color="textSecondary">
                      ID: {section.id?.slice(0, 8)}...
                    </Typography>
                  </Box>
                </CardContent>
              </Card>
            ))}
          </Box>

          {/* Bottom action */}
          <Card sx={{p: 2, bgcolor: '#f8fafc', border: '1px solid #e2e8f0'}}>
            <Box
              display="flex"
              justifyContent="space-between"
              alignItems="center"
              flexWrap="wrap"
              gap={2}>
              <Typography variant="body2" color="textSecondary">
                {sections.length} section(s) for {entityName || entityId}
              </Typography>
              <Button
                variant="contained"
                size="small"
                onClick={() =>
                  navigate(
                    `/admin/understand-it-better/edit/${entityType}/${entityId}`,
                  )
                }
                sx={{
                  bgcolor: '#1976d2',
                  color: '#fff',
                  '&:hover': {bgcolor: '#1565c0'},
                }}>
                Edit Sections
              </Button>
            </Box>
          </Card>
        </Box>
      </MDBox>
      <Footer />
    </DashboardLayout>
  );
};

export default UnderstandItBetterView;

// import React, {useEffect, useState} from 'react';
// import {useNavigate, useParams} from 'react-router-dom';
// import {Notify, Confirm, Loading} from 'notiflix';
// import moment from 'moment';
// import {
//   Box,
//   Button,
//   Card,
//   CardContent,
//   Chip,
//   CircularProgress,
//   Divider,
//   Grid,
//   Typography,
//   Alert,
// } from '@mui/material';
// import {
//   ArrowBack,
//   Delete as DeleteIcon,
//   Edit as EditIcon,
//   CalendarToday,
//   Update,
//   Label,
//   Notes,
//   Category,
// } from '@mui/icons-material';
// import MDBox from 'components/MDBox';
// import MDTypography from 'components/MDTypography';
// import DashboardLayout from 'examples/LayoutContainers/DashboardLayout';
// import DashboardNavbar from 'examples/Navbars/DashboardNavbar';
// import Footer from 'examples/Footer';
// import {understandItBetterApi} from 'api/understandItBetterApi';

// const ENTITY_TYPE_META = {
//   product: {
//     label: 'Product',
//     color: '#1565c0',
//     bg: '#e3f2fd',
//     lightBg: '#f0f7ff',
//   },
//   valuepack: {
//     label: 'Value Pack',
//     color: '#7b1fa2',
//     bg: '#f3e5f5',
//     lightBg: '#fdf4ff',
//   },
//   question_answer: {
//     label: 'Question & Answer',
//     color: '#2e7d32',
//     bg: '#e8f5e9',
//     lightBg: '#f0fdf4',
//   },
// };

// const InfoRow = ({icon: Icon, label, value, valueComponent}) => (
//   <Box display="flex" alignItems="flex-start" gap={2} py={1.5}>
//     <Box
//       sx={{
//         width: 36,
//         height: 36,
//         borderRadius: 1.5,
//         bgcolor: '#f1f5f9',
//         display: 'flex',
//         alignItems: 'center',
//         justifyContent: 'center',
//         flexShrink: 0,
//       }}>
//       <Icon sx={{color: '#64748b', fontSize: 18}} />
//     </Box>
//     <Box flex={1}>
//       <Typography
//         variant="caption"
//         color="textSecondary"
//         fontWeight={600}
//         textTransform="uppercase"
//         letterSpacing={0.5}>
//         {label}
//       </Typography>
//       {valueComponent || (
//         <Typography variant="body2" color="#1e293b" mt={0.25}>
//           {value || '—'}
//         </Typography>
//       )}
//     </Box>
//   </Box>
// );

// const UnderstandItBetterView = () => {
//   const {id} = useParams();
//   const navigate = useNavigate();

//   const [section, setSection] = useState(null);
//   const [loading, setLoading] = useState(true);
//   const [error, setError] = useState(null);
//   const [deleting, setDeleting] = useState(false);

//   useEffect(() => {
//     if (id) fetchSection();
//   }, [id]);

//   const fetchSection = async () => {
//     try {
//       setLoading(true);
//       setError(null);
//       const data = await understandItBetterApi.getById(id);
//       setSection(data);
//     } catch (err) {
//       setError(err.message || 'Failed to load section');
//       Notify.failure('Failed to load section details');
//     } finally {
//       setLoading(false);
//     }
//   };

//   const handleDelete = () => {
//     Confirm.show(
//       'Delete Section',
//       'Are you sure you want to delete this section?',
//       'Delete',
//       'Cancel',
//       async () => {
//         setDeleting(true);
//         Loading.circle('Deleting...');
//         try {
//           await understandItBetterApi.deleteOne(id);
//           Notify.success('Section deleted successfully');
//           navigate('/admin/understand-it-better');
//         } catch (err) {
//           Notify.failure(err.message || 'Failed to delete');
//         } finally {
//           Loading.remove();
//           setDeleting(false);
//         }
//       },
//       {okButtonBackground: '#ff6b6b', titleColor: '#ff6b6b'},
//     );
//   };

//   if (loading) {
//     return (
//       <DashboardLayout>
//         <DashboardNavbar />
//         <Box
//           display="flex"
//           justifyContent="center"
//           alignItems="center"
//           minHeight="60vh"
//           flexDirection="column">
//           <CircularProgress />
//           <Typography variant="h6" mt={2}>
//             Loading section...
//           </Typography>
//         </Box>
//         <Footer />
//       </DashboardLayout>
//     );
//   }

//   if (error || !section) {
//     return (
//       <DashboardLayout>
//         <DashboardNavbar />
//         <Box p={4}>
//           <Alert
//             severity="error"
//             action={
//               <Button
//                 color="inherit"
//                 size="small"
//                 onClick={() => navigate('/admin/understand-it-better')}>
//                 Back to List
//               </Button>
//             }>
//             {error || 'Section not found'}
//           </Alert>
//         </Box>
//         <Footer />
//       </DashboardLayout>
//     );
//   }

//   const entityMeta = ENTITY_TYPE_META[section.entityType] || {
//     label: section.entityType,
//     color: '#475569',
//     bg: '#f1f5f9',
//     lightBg: '#f8fafc',
//   };

//   return (
//     <DashboardLayout>
//       <DashboardNavbar />
//       <MDBox pt={6} pb={3}>
//         <Box mx="auto" px={2}>
//           {/* Back + Actions */}
//           <Box
//             display="flex"
//             alignItems="center"
//             justifyContent="space-between"
//             mb={4}
//             flexWrap="wrap"
//             gap={2}>
//             <Button
//               startIcon={<ArrowBack />}
//               onClick={() => navigate('/admin/understand-it-better')}>
//               Back to List
//             </Button>

//             <Box display="flex" gap={1.5}>
//               <Button
//                 variant="outlined"
//                 startIcon={<EditIcon />}
//                 onClick={() =>
//                   navigate(`/admin/understand-it-better/edit/${id}`)
//                 }
//                 sx={{borderColor: '#1976d2', color: '#1976d2'}}>
//                 Edit
//               </Button>
//               <Button
//                 variant="outlined"
//                 startIcon={
//                   deleting ? <CircularProgress size={16} /> : <DeleteIcon />
//                 }
//                 onClick={handleDelete}
//                 disabled={deleting}
//                 sx={{
//                   borderColor: '#ef4444',
//                   color: '#ef4444',
//                   '&:hover': {bgcolor: '#fef2f2', borderColor: '#dc2626'},
//                 }}>
//                 Delete
//               </Button>
//             </Box>
//           </Box>

//           {/* Main Detail Card */}
//           <Card sx={{mb: 3, overflow: 'hidden'}}>
//             {/* Colored top banner */}
//             <Box
//               sx={{
//                 bgcolor: entityMeta.color,
//                 p: 3,
//                 display: 'flex',
//                 alignItems: 'center',
//                 justifyContent: 'space-between',
//                 flexWrap: 'wrap',
//                 gap: 2,
//               }}>
//               <Box>
//                 <Typography
//                   variant="h5"
//                   fontWeight={700}
//                   color="#fff"
//                   gutterBottom>
//                   {section.title}
//                 </Typography>
//                 <Box display="flex" alignItems="center" gap={1.5}>
//                   <Chip
//                     label={entityMeta.label}
//                     size="small"
//                     sx={{
//                       bgcolor: 'rgba(255,255,255,0.2)',
//                       color: '#fff',
//                       fontWeight: 600,
//                       fontSize: '0.75rem',
//                     }}
//                   />
//                   <Chip
//                     label={section.status !== false ? 'Active' : 'Inactive'}
//                     size="small"
//                     sx={{
//                       bgcolor:
//                         section.status !== false
//                           ? 'rgba(134,239,172,0.3)'
//                           : 'rgba(252,165,165,0.3)',
//                       color: '#fff',
//                       fontWeight: 600,
//                       fontSize: '0.75rem',
//                       border: '1px solid rgba(255,255,255,0.4)',
//                     }}
//                   />
//                 </Box>
//               </Box>
//             </Box>

//             <CardContent sx={{p: 3}}>
//               {/* Description */}
//               <Box
//                 sx={{
//                   bgcolor: entityMeta.lightBg,
//                   border: `1px solid ${entityMeta.color}30`,
//                   borderRadius: 2,
//                   p: 2.5,
//                   mb: 3,
//                 }}>
//                 <Typography
//                   variant="subtitle2"
//                   fontWeight={700}
//                   color={entityMeta.color}
//                   mb={1}>
//                   Description
//                 </Typography>
//                 <Typography variant="body1" color="#374151" lineHeight={1.8}>
//                   {section.description}
//                 </Typography>
//               </Box>

//               <Divider sx={{mb: 2}} />

//               {/* Info rows */}
//               <Grid container>
//                 <Grid item xs={12} sm={6}>
//                   <InfoRow
//                     icon={Category}
//                     label="Entity Type"
//                     valueComponent={
//                       <Chip
//                         label={entityMeta.label}
//                         size="small"
//                         sx={{
//                           bgcolor: entityMeta.bg,
//                           color: entityMeta.color,
//                           fontWeight: 600,
//                           mt: 0.5,
//                         }}
//                       />
//                     }
//                   />
//                 </Grid>

//                 <Grid item xs={12} sm={6}>
//                   <InfoRow
//                     icon={Label}
//                     label="Entity Name / ID"
//                     value={section.entityName || section.entityId}
//                   />
//                 </Grid>

//                 <Grid item xs={12} sm={6}>
//                   <InfoRow
//                     icon={CalendarToday}
//                     label="Created At"
//                     value={
//                       section.createdAt
//                         ? moment(section.createdAt).format(
//                             'DD MMM YYYY, hh:mm A',
//                           )
//                         : 'N/A'
//                     }
//                   />
//                 </Grid>

//                 <Grid item xs={12} sm={6}>
//                   <InfoRow
//                     icon={Update}
//                     label="Last Updated"
//                     value={
//                       section.updatedAt
//                         ? moment(section.updatedAt).format(
//                             'DD MMM YYYY, hh:mm A',
//                           )
//                         : 'N/A'
//                     }
//                   />
//                 </Grid>

//                 <Grid item xs={12} sm={6}>
//                   <InfoRow icon={Notes} label="Section ID" value={section.id} />
//                 </Grid>
//               </Grid>
//             </CardContent>
//           </Card>

//           {/* Quick actions bottom */}
//           <Card sx={{p: 2, bgcolor: '#f8fafc', border: '1px solid #e2e8f0'}}>
//             <Box
//               display="flex"
//               justifyContent="space-between"
//               alignItems="center"
//               flexWrap="wrap"
//               gap={2}>
//               <Typography variant="body2" color="textSecondary">
//                 Visit the list page to view more sections for this entity
//               </Typography>
//               <Button
//                 variant="contained"
//                 size="small"
//                 onClick={() => navigate('/admin/understand-it-better')}
//                 sx={{
//                   bgcolor: '#1976d2',
//                   color: '#fff',
//                   '&:hover': {bgcolor: '#1565c0'},
//                 }}>
//                 View All Sections
//               </Button>
//             </Box>
//           </Card>
//         </Box>
//       </MDBox>
//       <Footer />
//     </DashboardLayout>
//   );
// };

// export default UnderstandItBetterView;
